import React from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket,
  Brain,
  CreditCard,
  Zap,
  Wrench,
  HelpCircle,
  Users,
  Image,
  Lightbulb,
  LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { HelpCategory } from '@/services/helpService';

const iconMap: Record<string, LucideIcon> = {
  Rocket,
  Brain,
  CreditCard,
  Zap,
  Wrench,
  HelpCircle,
  Users,
  Image,
  Lightbulb,
};

const colorMap: Record<string, { bg: string; text: string; hover: string }> = {
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', hover: 'group-hover:bg-purple-200' },
  violet: { bg: 'bg-violet-100', text: 'text-violet-600', hover: 'group-hover:bg-violet-200' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', hover: 'group-hover:bg-blue-200' },
  green: { bg: 'bg-green-100', text: 'text-green-600', hover: 'group-hover:bg-green-200' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600', hover: 'group-hover:bg-orange-200' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600', hover: 'group-hover:bg-yellow-200' },
  red: { bg: 'bg-red-100', text: 'text-red-600', hover: 'group-hover:bg-red-200' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600', hover: 'group-hover:bg-gray-200' },
};

interface CategoryCardProps {
  category: HelpCategory;
}

export function CategoryCard({ category }: CategoryCardProps) {
  const Icon = iconMap[category.icon] || HelpCircle;
  const colors = colorMap[category.color] || colorMap.gray;

  return (
    <Link
      to={`/help/${category.slug}`}
      className="group block p-5 rounded-2xl border border-gray-200 bg-white hover:border-violet-300 hover:shadow-md transition-all"
    >
      <div
        className={cn(
          'w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors',
          colors.bg,
          colors.hover
        )}
      >
        <Icon className={cn('w-6 h-6', colors.text)} />
      </div>
      <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-violet-700 transition-colors">
        {category.name}
      </h3>
      <p className="text-sm text-gray-500 mb-3 line-clamp-2">
        {category.description}
      </p>
      <span className="text-xs text-gray-400">
        {category.article_count} {category.article_count === 1 ? 'article' : 'articles'}
      </span>
    </Link>
  );
}
